import { useEffect, useState } from "react";
import { apiFetch } from "./api.js";

interface MeOrg {
  id: string;
  name: string;
  slug?: string;
  logo?: string | null;
  role?: string;
  ownerEmail?: string | null;
}
interface MeUser {
  id: string;
  name: string | null;
  email: string;
  image?: string | null;
}
interface MeTeam {
  id: string;
  name: string;
  league?: string | null;
}
interface MeResponse {
  user?: MeUser;
  organization: MeOrg | null;
  organizations: MeOrg[];
  activeTeam?: MeTeam | null;
  teams?: MeTeam[];
}

/**
 * Module-level cache for the active club + user identity. Multiple
 * `<AppShell>`s (and the home cockpit) share the same fetch so we don't
 * hammer /me on every route change.
 *
 * Additionally persisted to localStorage so the *next* app start hydrates
 * instantly from disk — no flicker between "anonymous fallback" and the
 * real coach/club name. This is the PWA-style offline-first behaviour the
 * native build already enjoys via the local SQLite mirror.
 */
interface CachedMe {
  orgId: string | null;
  orgName: string | null;
  orgLogo: string | null;
  orgRole: string | null;
  ownerEmail: string | null;
  userName: string | null;
  userImage: string | null;
  activeTeamId: string | null;
  activeTeamName: string | null;
  activeTeamLeague: string | null;
  /** Every org the signed-in coach is a member of. */
  organizations: MeOrg[];
  /** Teams the caller can pick within the active org. */
  teams: MeTeam[];
}

const STORAGE_KEY = "coach.me.v1";
const EMPTY: CachedMe = {
  orgId: null,
  orgName: null,
  orgLogo: null,
  orgRole: null,
  ownerEmail: null,
  userName: null,
  userImage: null,
  activeTeamId: null,
  activeTeamName: null,
  activeTeamLeague: null,
  organizations: [],
  teams: [],
};

function readStorage(): CachedMe | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedMe> | null;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      orgId: typeof parsed.orgId === "string" ? parsed.orgId : null,
      orgName: typeof parsed.orgName === "string" ? parsed.orgName : null,
      orgLogo: typeof parsed.orgLogo === "string" ? parsed.orgLogo : null,
      orgRole: typeof parsed.orgRole === "string" ? parsed.orgRole : null,
      ownerEmail:
        typeof parsed.ownerEmail === "string" ? parsed.ownerEmail : null,
      userName: typeof parsed.userName === "string" ? parsed.userName : null,
      userImage: typeof parsed.userImage === "string" ? parsed.userImage : null,
      activeTeamId:
        typeof parsed.activeTeamId === "string" ? parsed.activeTeamId : null,
      activeTeamName:
        typeof parsed.activeTeamName === "string"
          ? parsed.activeTeamName
          : null,
      activeTeamLeague:
        typeof parsed.activeTeamLeague === "string"
          ? parsed.activeTeamLeague
          : null,
      organizations: Array.isArray(parsed.organizations)
        ? parsed.organizations
        : [],
      teams: Array.isArray(parsed.teams) ? parsed.teams : [],
    };
  } catch {
    return null;
  }
}

function writeStorage(value: CachedMe): void {
  if (typeof window === "undefined") return;
  try {
    // Only persist when at least one field has a real value — avoid
    // overwriting a good cache with an empty failure-state object.
    const hasAny =
      value.orgName ||
      value.orgLogo ||
      value.userName ||
      value.userImage ||
      value.activeTeamId ||
      value.activeTeamName ||
      value.activeTeamLeague;
    if (!hasAny) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage full / disabled — silent.
  }
}

let cached: CachedMe | null = readStorage();
let inflight: Promise<CachedMe> | null = null;

function fetchOnce(): Promise<CachedMe> {
  if (!inflight) {
    inflight = apiFetch<MeResponse>("/api/v1/me", {
      credentials: "include",
    })
      .then((m): CachedMe => {
        const org = m.organization ?? m.organizations[0] ?? null;
        const next: CachedMe = {
          orgId: org?.id ?? null,
          orgName: org?.name ?? null,
          orgLogo: org?.logo ?? null,
          orgRole: org?.role ?? null,
          ownerEmail:
            (m.organization as { ownerEmail?: string | null } | null)
              ?.ownerEmail ?? null,
          userName: m.user?.name ?? null,
          userImage: m.user?.image ?? null,
          activeTeamId: m.activeTeam?.id ?? null,
          activeTeamName: m.activeTeam?.name ?? null,
          activeTeamLeague: m.activeTeam?.league ?? null,
          organizations: m.organizations ?? [],
          teams: m.teams ?? [],
        };
        cached = next;
        writeStorage(next);
        return next;
      })
      .catch((): CachedMe => {
        // Offline / unauthenticated: keep whatever we previously cached
        // so the UI stays populated. Only return EMPTY when we truly
        // have nothing on disk.
        return cached ?? EMPTY;
      })
      .finally(() => {
        // Allow a fresh fetch on next mount if the call failed entirely
        // and we never populated the cache.
        if (cached === null) inflight = null;
      });
  }
  return inflight;
}

function useMeCached(): CachedMe {
  // Hydrate synchronously from the module cache (which itself read
  // localStorage on import) so the first render already has real data.
  const [state, setState] = useState<CachedMe>(cached ?? EMPTY);

  useEffect(() => {
    let cancelled = false;
    void fetchOnce().then((next) => {
      if (cancelled) return;
      // Only re-render when something actually changed — avoids a flicker
      // when /me returns the exact same payload we already had on disk.
      setState((prev) => {
        if (
          prev.orgName === next.orgName &&
          prev.orgLogo === next.orgLogo &&
          prev.userName === next.userName &&
          prev.userImage === next.userImage &&
          prev.activeTeamId === next.activeTeamId &&
          prev.activeTeamName === next.activeTeamName &&
          prev.activeTeamLeague === next.activeTeamLeague
        ) {
          return prev;
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function useActiveOrgName(): string | null {
  return useMeCached().orgName;
}

export function useActiveOrgLogo(): string | null {
  return useMeCached().orgLogo;
}

/** Optional free-form league of the currently active team. */
export function useActiveTeamLeague(): string | null {
  return useMeCached().activeTeamLeague;
}

/**
 * Invalidate the cached `/me` snapshot so the next mount of any
 * `useActive*` hook re-fetches fresh data. Call this after a server
 * mutation that changes club metadata (name / logo / league) or user
 * profile, so the home cockpit picks up the change without a reload.
 */
export function invalidateActiveMe(): void {
  inflight = null;
  void fetchOnce();
}

export function useActiveUserName(): string | null {
  return useMeCached().userName;
}

export function useActiveUserImage(): string | null {
  return useMeCached().userImage;
}

/** Currently active team id (from session.activeTeamId, resolved server-side). */
export function useActiveTeamId(): string | null {
  return useMeCached().activeTeamId;
}

/** Display name of the currently active team — used as header subtitle. */
export function useActiveTeamName(): string | null {
  return useMeCached().activeTeamName;
}

/** Id of the currently active org (when resolved server-side). */
export function useActiveOrgId(): string | null {
  return useMeCached().orgId;
}

/** Every org the signed-in coach is a member of. */
export function useMyOrganizations(): MeOrg[] {
  return useMeCached().organizations;
}

/** Teams in the active org the caller can pick. */
export function useMyTeams(): MeTeam[] {
  return useMeCached().teams;
}

export function useActiveOrgRole(): string | null {
  return useMeCached().orgRole;
}

export function useActiveOrgOwnerEmail(): string | null {
  return useMeCached().ownerEmail;
}
